import { AppealStatus, GroupByOption, WriteOffCategory, WriteOffStatus } from '@repo/types';
import { reject, values } from 'lodash';

import { ArWriteOffFilter } from '../types/writeOffs';

/**
 * Default subtab to display when navigating to write-offs page
 */
export const DEFAULT_WRITE_OFF_SUBTAB = 'proposed';

/**
 * Mapping of subtab IDs to their corresponding write-off statuses
 */
export const WRITE_OFF_SUBTAB_TO_STATUS: Record<string, WriteOffStatus[]> = {
  proposed: [WriteOffStatus.PENDING],
  approved: [WriteOffStatus.APPROVED],
  rejected: [WriteOffStatus.REJECTED],
  processed: [WriteOffStatus.ARCHIVED, WriteOffStatus.CLOSED],
  escalation: [WriteOffStatus.ESCALATED],
  all: [
    WriteOffStatus.PENDING,
    WriteOffStatus.ESCALATED,
    WriteOffStatus.APPROVED,
    WriteOffStatus.REJECTED,
    WriteOffStatus.ARCHIVED,
    WriteOffStatus.CLOSED,
  ],
};

/**
 * Action dialog modes for write-off operations
 */
export const WRITE_OFF_ACTION_MODES = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ARCHIVED: 'archived',
  NOTE: 'note',
} as const;

export type WriteOffActionMode =
  | typeof WRITE_OFF_ACTION_MODES.APPROVED
  | typeof WRITE_OFF_ACTION_MODES.REJECTED
  | typeof WRITE_OFF_ACTION_MODES.ARCHIVED
  | typeof WRITE_OFF_ACTION_MODES.NOTE;

/**
 * Write-off subtab configuration
 */
export const WRITE_OFF_SUBTABS = {
  PROPOSED: 'proposed',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  PROCESSED: 'processed',
  ALL: 'all',
} as const;

export type WriteOffSubtab =
  | typeof WRITE_OFF_SUBTABS.PROPOSED
  | typeof WRITE_OFF_SUBTABS.APPROVED
  | typeof WRITE_OFF_SUBTABS.REJECTED
  | typeof WRITE_OFF_SUBTABS.PROCESSED
  | typeof WRITE_OFF_SUBTABS.ALL;

// ============================================================================
// Constants migrated from components/write-offs/shared/constants.ts
// ============================================================================

/**
 * Default appeal statuses to show initially (all except Closed)
 */
export const DEFAULT_APPEAL_STATUSES = reject(
  values(AppealStatus),
  status => status === AppealStatus.CLOSED
);

/**
 * Initial filter values for write-off tables
 */
export const INITIAL_FILTERS: ArWriteOffFilter = {
  groupBy: GroupByOption.CLIENT,
  daysPastDue: 30,
  search: '',
  appealStatus: DEFAULT_APPEAL_STATUSES,
};

/**
 * Common CSS class for table cells
 */
export const CELL_STYLE = 'text-sm text-xtnd-dark dark:text-xtnd-light';

/**
 * Options for filtering by days past due
 */
export const DAYS_PAST_DUE_OPTIONS = [
  { label: 'Current', value: '0' },
  { label: 'All Past Due', value: '1' },
  { label: '30+ days', value: '30' },
  { label: '60+ days', value: '60' },
  { label: '90+ days', value: '90' },
  { label: '120+ days', value: '120' },
];

/**
 * Options for filtering by write-off category
 */
export const CATEGORY_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Write Off', value: WriteOffCategory.WRITE_OFF },
  { label: 'Bad Debt', value: WriteOffCategory.BAD_DEBT },
];

/**
 * Default props for write-off DataTable components
 */
export const WRITE_OFF_TABLE_DEFAULT_PROPS = {
  stickyHeader: true,
  tableContainerClassName: 'overflow-y-auto relative',
  tableClassName: 'min-w-full divide-y divide-gray-200 relative',
  pageSizeOptions: [25, 50, 100, 200] as number[],
  getRowKey: (_: unknown, index: number) => `row-${index}`,
  loadingRowCount: 10,
  loadingColumnCount: 6,
};

// ============================================================================
// Write-off Status Badge Constants
// ============================================================================

/**
 * Write-off status labels
 */
export const WRITEOFF_STATUS_LABEL = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  ARCHIVED: 'Archived',
  CLOSED: 'Closed',
  ESCALATED: 'Escalated',
};

/**
 * Write-off status text colors for light/dark mode
 * Reuses colors from Appeal Status:
 * - PENDING: Uses AWAITING_PAYMENT colors (yellow/amber)
 * - APPROVED: Uses APPEAL_APPROVED colors (green)
 * - REJECTED: Uses APPEAL_DENIED colors (red)
 * - ARCHIVED/CLOSED: Uses CLOSED colors (gray)
 */
export const WRITEOFF_STATUS_TEXT_COLOR = {
  PENDING: { light: '#804F00', dark: '#FBF3C8' },
  APPROVED: { light: '#065F46', dark: '#D1FAE5' },
  REJECTED: { light: '#991B1B', dark: '#FEE2E2' },
  ARCHIVED: { light: '#4B5563', dark: '#D1D5DB' },
  CLOSED: { light: '#4B5563', dark: '#D1D5DB' },
  ESCALATED: { light: '#5B21B6', dark: '#E9D5FF' },
} as const;

/**
 * Write-off status options with light/dark mode support
 * Following the same pattern as APPEAL_STATUS_OPTIONS
 */
export const WRITEOFF_STATUS_OPTIONS = {
  light: {
    PENDING: {
      value: 'PENDING',
      label: WRITEOFF_STATUS_LABEL.PENDING,
      color: WRITEOFF_STATUS_TEXT_COLOR.PENDING.light,
      colorClass: 'text-[#804F00] bg-[#FBF3C8] border-[#804F00]/50',
    },
    APPROVED: {
      value: 'APPROVED',
      label: WRITEOFF_STATUS_LABEL.APPROVED,
      color: WRITEOFF_STATUS_TEXT_COLOR.APPROVED.light,
      colorClass: 'text-[#065F46] bg-[#065F46]/30 border-[#065F46]/50',
    },
    REJECTED: {
      value: 'REJECTED',
      label: WRITEOFF_STATUS_LABEL.REJECTED,
      color: WRITEOFF_STATUS_TEXT_COLOR.REJECTED.light,
      colorClass: 'text-[#991B1B] bg-[#991B1B]/30 border-[#991B1B]/50',
    },
    ARCHIVED: {
      value: 'ARCHIVED',
      label: WRITEOFF_STATUS_LABEL.ARCHIVED,
      color: WRITEOFF_STATUS_TEXT_COLOR.ARCHIVED.light,
      colorClass: 'text-[#4B5563] bg-[#4B5563]/30 border-[#4B5563]/50',
    },
    CLOSED: {
      value: 'CLOSED',
      label: WRITEOFF_STATUS_LABEL.CLOSED,
      color: WRITEOFF_STATUS_TEXT_COLOR.CLOSED.light,
      colorClass: 'text-[#4B5563] bg-[#4B5563]/30 border-[#4B5563]/50',
    },
    ESCALATED: {
      value: 'ESCALATED',
      label: WRITEOFF_STATUS_LABEL.ESCALATED,
      color: WRITEOFF_STATUS_TEXT_COLOR.ESCALATED.light,
      colorClass: 'text-[#5B21B6] bg-[#5B21B6]/10 border-[#5B21B6]/50',
    },
  },
  dark: {
    PENDING: {
      value: 'PENDING',
      label: WRITEOFF_STATUS_LABEL.PENDING,
      color: '#60A5FA', // Blue-400
      colorClass: 'text-white bg-[#1D4ED8]/50 border border-blue-800', // Blue background
    },
    APPROVED: {
      value: 'APPROVED',
      label: WRITEOFF_STATUS_LABEL.APPROVED,
      color: WRITEOFF_STATUS_TEXT_COLOR.APPROVED.dark,
      colorClass: 'text-white bg-[#065F46]/50 border-[#065F46]',
    },
    REJECTED: {
      value: 'REJECTED',
      label: WRITEOFF_STATUS_LABEL.REJECTED,
      color: WRITEOFF_STATUS_TEXT_COLOR.REJECTED.dark,
      colorClass: 'text-white bg-[#991B1B]/50 border-[#991B1B]',
    },
    ARCHIVED: {
      value: 'ARCHIVED',
      label: WRITEOFF_STATUS_LABEL.ARCHIVED,
      color: WRITEOFF_STATUS_TEXT_COLOR.ARCHIVED.dark,
      colorClass: 'text-white bg-[#4B5563]/50 border-[#4B5563]',
    },
    CLOSED: {
      value: 'CLOSED',
      label: WRITEOFF_STATUS_LABEL.CLOSED,
      color: WRITEOFF_STATUS_TEXT_COLOR.CLOSED.dark,
      colorClass: 'text-white bg-[#4B5563]/50 border-[#4B5563]',
    },
    ESCALATED: {
      value: 'ESCALATED',
      label: WRITEOFF_STATUS_LABEL.ESCALATED,
      color: WRITEOFF_STATUS_TEXT_COLOR.ESCALATED.dark,
      colorClass: 'text-white bg-[#5B21B6]/50 border-[#5B21B6]',
    },
  },
} as const;

// ============================================================================
// Write-off Category Badge Constants
// ============================================================================

/**
 * Write-off category labels
 */
export const WRITEOFF_CATEGORY_LABEL = {
  WRITE_OFF: 'Write Off',
  BAD_DEBT: 'Bad Debt',
};

/**
 * Write-off category color classes for light/dark mode
 */
export const WRITEOFF_CATEGORY_CLASSES = {
  WRITE_OFF: {
    light: 'text-[#1E40AF] bg-[#1E40AF]/20 border-[#1E40AF]/40',
    dark: 'text-[#FFFFFF] bg-[#1E40AF] border-[#1E40AF]',
  },
  BAD_DEBT: {
    light: 'text-[#991B1B] bg-[#991B1B]/30 border-[#991B1B]/50',
    dark: 'text-[#FFFFFF] bg-[#991B1B] border-[#991B1B]',
  },
};

// ============================================================================
// Write-off Type Badge Constants
// ============================================================================

/**
 * Write-off type labels
 */
export const WRITEOFF_TYPE_LABEL = {
  PROMPT_PAYMENT: 'Prompt Payment',
  BULK_DISCOUNT: 'Bulk Discount',
  ADMIN_FEE: 'Admin Fee',
  CLIENT_REFUSED: 'Client Refused',
  AUTO_PROPOSED: 'Auto Proposed',
  CUTS_APPLIED: 'Cuts Applied',
};

/**
 * Write-off type color classes for light/dark mode
 */
export const WRITEOFF_TYPE_CLASSES = {
  PROMPT_PAYMENT: {
    light: 'text-[#065F46] bg-[#065F46]/30 border-[#065F46]/50',
    dark: 'text-[#FFFFFF] bg-[#065F46] border-[#065F46]',
  },
  BULK_DISCOUNT: {
    light: 'text-[#3b82f5] bg-[#3b82f5]/20 border-[#3b82f5]/40',
    dark: 'text-[#FFFFFF] bg-[#3b82f5] border-[#3b82f5]',
  },
  ADMIN_FEE: {
    light: 'text-[#804F00] bg-[#804F00]/30 border-[#804F00]/50',
    dark: 'text-[#FFFFFF] bg-[#804F00] border-[#804F00]',
  },
  CLIENT_REFUSED: {
    light: 'text-[#991B1B] bg-[#991B1B]/30 border-[#991B1B]/50',
    dark: 'text-[#FFFFFF] bg-[#991B1B] border-[#991B1B]',
  },
  CUTS_APPLIED: {
    light: 'text-[#991B1B] bg-[#991B1B]/30 border-[#991B1B]/50',
    dark: 'text-[#FFFFFF] bg-[#991B1B] border-[#991B1B]',
  },
  AUTO_PROPOSED: {
    light: 'text-[#5B21B6] bg-[#5B21B6]/30 border-[#5B21B6]/50',
    dark: 'text-[#FFFFFF] bg-[#5B21B6] border-[#5B21B6]',
  },
};
